import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { isEmailVerified } from "../../utils/emailVerification";
import { 
  AcademicCapIcon, 
  PhotoIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from "../../components/icons/OutlineIcons";
import MediaUpload from "../../components/MediaUpload";

export default function CreateCourse() {
  const { user, emailVerified } = useAuthContext();
  const nav = useNavigate();

  // Expired users can see the page but create button will be disabled

  // Redirect if email not verified
  useEffect(() => {
    if (user && !emailVerified) {
      alert("กรุณายืนยันอีเมลของคุณก่อนสร้างคอร์ส");
      nav("/verify-email");
    }
  }, [user, emailVerified, nav]);

  const [activeTab, setActiveTab] = useState("basic");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [category, setCategory] = useState("");
  const [thumbnailUploaded, setThumbnailUploaded] = useState(false);
  const [tempCourseId] = useState(`temp_${Date.now()}`);

  const [loading, setLoading] = useState(false);

  const categories = [
    "การพัฒนาเว็บไซต์",
    "การออกแบบกราฟิก",
    "การตลาดดิจิทัล",
    "ภาษาต่างประเทศ",
    "ธุรกิจและการจัดการ",
    "การพัฒนาตนเอง",
    "ศิลปะและงานฝีมือ",
    "สุขภาพและฟิตเนส",
    "อื่นๆ"
  ];

  const submit = async () => {
    if (!user) return;
    
    // Check email verification
    if (!auth.currentUser || !isEmailVerified(auth.currentUser)) {
      alert("กรุณายืนยันอีเมลของคุณก่อนสร้างคอร์ส");
      nav("/verify-email");
      return;
    }
    
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    setLoading(true);

    try {
      const randomId = Math.random().toString(36).substring(2, 8).toUpperCase();
      const courseId = `${user.uid.substring(0, 6)}-${randomId}`;
      
      const ref = await addDoc(collection(db, "courses"), {
        courseId,
        title,
        description: desc,
        price: Number(price),
        thumbnail,
        category,
        instructorId: user.uid,
        createdAt: serverTimestamp(),
      });

      setLoading(false);
      nav(`/instructor/courses/${ref.id}/edit`);
    } catch (error) {
      console.error("Error creating course:", error);
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return title.trim() !== "" && desc.trim() !== "" && category !== "" && price !== "" && thumbnailUploaded;
  };

  const canAccessTab = (tab: string) => {
    if (tab === "basic") return true;
    if (tab === "category") return title.trim() !== "" && desc.trim() !== "";
    if (tab === "media") return title.trim() !== "" && desc.trim() !== "" && category !== "";
    if (tab === "pricing") return title.trim() !== "" && desc.trim() !== "" && category !== "" && thumbnailUploaded;
    return false;
  };

  const goToNextTab = () => {
    if (activeTab === "basic" && title.trim() && desc.trim()) {
      setActiveTab("category");
    } else if (activeTab === "category" && category) {
      setActiveTab("media");
    } else if (activeTab === "media" && thumbnailUploaded) {
      setActiveTab("pricing");
    }
  };

  const goToPrevTab = () => {
    if (activeTab === "pricing") setActiveTab("media");
    else if (activeTab === "media") setActiveTab("category");
    else if (activeTab === "category") setActiveTab("basic");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => nav(-1)}
            className="flex items-center gap-2 text-textLight hover:text-textDark transition-colors mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            กลับ
          </button>
          <h1 className="text-4xl font-bold text-textDark mb-2">สร้างคอร์สใหม่</h1>
          <p className="text-textLight text-lg">ปรับปรุงและจัดการคอร์สของคุณ</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    activeTab === "basic"
                      ? "bg-gradient-primary text-white shadow-lg scale-110"
                      : title && desc ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {title && desc && activeTab !== "basic" ? <CheckCircleIcon className="w-6 h-6" /> : "1"}
                </div>
                <span
                  className={`mt-2 text-sm font-medium ${
                    activeTab === "basic" ? "text-primary" : title && desc ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  ข้อมูลพื้นฐาน
                </span>
              </div>
              <div
                className={`h-1 flex-1 mx-4 rounded transition-all duration-300 ${
                  title && desc ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            </div>
            <div className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    activeTab === "category"
                      ? "bg-gradient-primary text-white shadow-lg scale-110"
                      : category ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {category && activeTab !== "category" ? <CheckCircleIcon className="w-6 h-6" /> : "2"}
                </div>
                <span
                  className={`mt-2 text-sm font-medium ${
                    activeTab === "category" ? "text-primary" : category ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  หมวดหมู่
                </span>
              </div>
              <div
                className={`h-1 flex-1 mx-4 rounded transition-all duration-300 ${
                  category ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            </div>
            <div className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    activeTab === "media"
                      ? "bg-gradient-primary text-white shadow-lg scale-110"
                      : thumbnailUploaded ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {thumbnailUploaded && activeTab !== "media" ? <CheckCircleIcon className="w-6 h-6" /> : "3"}
                </div>
                <span
                  className={`mt-2 text-sm font-medium ${
                    activeTab === "media" ? "text-primary" : thumbnailUploaded ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  รูปภาพและสื่อ
                </span>
              </div>
              <div
                className={`h-1 flex-1 mx-4 rounded transition-all duration-300 ${
                  thumbnailUploaded ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            </div>
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                  activeTab === "pricing"
                    ? "bg-gradient-primary text-white shadow-lg scale-110"
                    : price ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"
                }`}
              >
                {price && activeTab !== "pricing" ? <CheckCircleIcon className="w-6 h-6" /> : "4"}
              </div>
              <span
                className={`mt-2 text-sm font-medium ${
                  activeTab === "pricing" ? "text-primary" : price ? "text-green-600" : "text-gray-400"
                }`}
              >
                ราคาและการขาย
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <AcademicCapIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-textDark">ข้อมูลพื้นฐาน</h2>
                    <p className="text-textLight">บอกเราเกี่ยวกับคอร์สของคุณ</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-textDark mb-2">
                    ชื่อคอร์ส *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="เช่น: เรียนรู้ React และ TypeScript สำหรับมือใหม่"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors text-lg"
                    required
                  />
                  <p className="text-sm text-textLight mt-2">
                    ชื่อที่ดีควรชัดเจนและบอกถึงสิ่งที่นักเรียนจะได้เรียนรู้
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-textDark mb-2">
                    คำอธิบายคอร์ส *
                  </label>
                  <textarea
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="อธิบายว่าคอร์สนี้สอนอะไร นักเรียนจะได้เรียนรู้อะไรบ้าง และเหมาะกับใคร"
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors resize-none"
                    required
                  />
                  <p className="text-sm text-textLight mt-2">
                    {desc.length} / 500 ตัวอักษร (แนะนำอย่างน้อย 100 ตัวอักษร)
                  </p>
                </div>
              </div>
            )}

            {/* Category Tab */}
            {activeTab === "category" && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <AcademicCapIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-textDark">เลือกหมวดหมู่</h2>
                    <p className="text-textLight">ช่วยให้นักเรียนค้นหาคอร์สของคุณได้ง่ายขึ้น</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-300 ${
                        category === cat
                          ? "border-primary bg-blue-50 shadow-lg scale-105"
                          : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                      }`}
                    >
                      <div className="font-bold text-textDark">{cat}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === "media" && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <PhotoIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-textDark">รูปภาพและสื่อ</h2>
                    <p className="text-textLight">จัดการรูปภาพปกและสื่อต่างๆ</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-textDark mb-2">
                    รูปภาพปกคอร์ส *
                  </label>
                  {!thumbnailUploaded ? (
                    <MediaUpload
                      courseId={tempCourseId}
                      mediaType="image"
                      onUploadComplete={(mediaId, url) => {
                        setThumbnail(url);
                        setThumbnailUploaded(true);
                      }}
                      onUploadError={(error) => alert(error)}
                      aspectRatio={16 / 9}
                    />
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircleIcon className="w-5 h-5" />
                          <span className="font-semibold">อัปโหลดรูปภาพสำเร็จ!</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setThumbnail("");
                          setThumbnailUploaded(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                      >
                        อัปโหลดรูปภาพใหม่
                      </button>
                    </div>
                  )}
                  <p className="text-sm text-textLight mt-2">
                    แนะนำขนาด 1280x720 พิกเซล (16:9) • รองรับ .jpg, .png, .webp
                  </p>
                  {thumbnail && thumbnailUploaded && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-textDark mb-2">ตัวอย่าง:</p>
                      <img
                        src={thumbnail}
                        alt="Course thumbnail preview"
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200"
                        onError={(e) => {
                          e.currentTarget.src = "https://via.placeholder.com/1280x720?text=Invalid+Image+URL";
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === "pricing" && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <CurrencyDollarIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-textDark">ราคาและการขาย</h2>
                    <p className="text-textLight">กำหนดราคาและตัวเลือกการขาย</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-textDark mb-2">
                    ราคาคอร์ส (บาท) *
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                      <CurrencyDollarIcon className="w-6 h-6 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="1"
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors text-lg"
                    />
                  </div>
                  <p className="text-sm text-textLight mt-2">
                    ใส่ 0 หากต้องการให้คอร์สฟรี
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-blue-100">
                  <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
                    <span className="text-2xl">💰</span>
                    เคล็ดลับการตั้งราคา
                  </h3>
                  <ul className="space-y-2 text-sm text-textLight">
                    <li>• วิเคราะห์ราคาคอร์สที่คล้ายกันในตลาด</li>
                    <li>• พิจารณาความยาวและคุณภาพของเนื้อหา</li>
                    <li>• เริ่มต้นด้วยราคาที่เหมาะสม แล้วค่อยปรับตามผลตอบรับ</li>
                    <li>• คอร์สฟรีช่วยดึงดูดนักเรียนและสร้างชื่อเสียง</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-gray-100">
              <button
                onClick={goToPrevTab}
                disabled={activeTab === "basic"}
                className="px-6 py-3 rounded-xl font-semibold text-textDark bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← ย้อนกลับ
              </button>

              {activeTab === "pricing" ? (
                <button
                  onClick={submit}
                  disabled={loading || !isFormValid()}
                  className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-primary hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังสร้างคอร์ส...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      สร้างคอร์ส
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={goToNextTab}
                  disabled={
                    (activeTab === "basic" && (!title.trim() || !desc.trim())) ||
                    (activeTab === "category" && !category) ||
                    (activeTab === "media" && !thumbnailUploaded)
                  }
                  className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-primary hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  ถัดไป →
                </button>
              )}
            </div>
        </div>

        {/* Tips */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-blue-100">
          <h3 className="font-bold text-textDark mb-3 flex items-center gap-2">
            <span className="text-2xl">💡</span>
            เคล็ดลับ
          </h3>
          <ul className="space-y-2 text-sm text-textLight">
            <li>• ชื่อคอร์สควรสั้น กระชับ และบอกถึงสิ่งที่จะได้เรียนรู้</li>
            <li>• คำอธิบายควรมีรายละเอียดเพียงพอให้นักเรียนเข้าใจว่าจะได้อะไร</li>
            <li>• เลือกหมวดหมู่ที่เหมาะสมเพื่อให้นักเรียนค้นหาเจอง่าย</li>
            <li>• รูปภาพปกที่ดูดีจะช่วยดึงดูดนักเรียนได้มากขึ้น</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
